import { HStack, VStack, XDiv } from "IZOX/_X";
import { ColorX, LogicX, ResponsiveX, ZMath } from "IZOX/STATIC";
import React, { Children, cloneElement, useCallback, useEffect, useRef, useState } from "react";
import QPMenuItem from "./QPMenuItem";
import { SysConfig } from "__SYSDefault/Config";

/**
 * @typedef {Object} OPMenuParams
 * @property {JSX.Element} children
 * @property {String} defaultTab
 * @property {String | Number} defaultTabWidth
 * 
 * @param {OPMenuParams} param0 
 * @returns 
 */
function QPMenu({
  children,
  defaultTab = "",
  defaultTabWidth = 250,
  defaultShowTab = false,
  minWidth = 250,
  maxWidth = 1000,
  setMenuWidth,
  darkMode,
  responsive,
  onMounted
}){

  const [tabWidth, setTabWidth] = useState(defaultTabWidth);
  const [tab, setTab] = useState(defaultTab);
  const [showTab, setShowTab] = useState(defaultShowTab);
  const [hover, setHover] = useState(false);
  const resizableRef = useRef(null);
  const isResizing = useRef(false);

  const _set = useCallback((showtab, _tabWidth) => {
    setShowTab(showtab);
    setTabWidth(_tabWidth);
    setMenuWidth?.(50 + (showtab ? _tabWidth : 0));
  }, [setMenuWidth]);

  const _setTab = useCallback((_tab, force = false) => {
    setTab(_tab);
    let _newWidth = ZMath.Cap(tabWidth, minWidth, maxWidth);
    if(_tab === tab && !force){
      _set(!showTab, _newWidth);
    }else{
      _set(true, _newWidth);
    }
  }, [tab, showTab, tabWidth, minWidth, maxWidth, _set]);

  const _hideTab = useCallback(() => {
    _set(false, tabWidth);
  }, [_set, tabWidth]);

  useEffect(() => {
  
    let callbacks = {
      SetTab: _setTab,
      HideTab: _hideTab
    };
  
    onMounted?.(callbacks);
  }, [onMounted, _setTab, _hideTab]);

  function renderChildren(_c){
    return Children.map(_c, (o, i) => {
      if (!React.isValidElement(o)) return o;
      let addOns = {
        children: renderChildren(o?.props?.children),
      };
      if(LogicX.isOneOf(o.type, [QPMenuItem])){
        addOns = {
          ...addOns,
          _tab: tab,
          _setTab: _setTab,
          _showTab: showTab,
          darkMode: darkMode,
          responsive: responsive
        };
      }
      return cloneElement(o, addOns);
    });
  }

  function renderMain(_c){
    let _target = _c.find(o => o.props.tab === tab);
    return _target ? _target.props.children : null;
  }

  const handleMouseDown = (e) => {
    isResizing.current = true;
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const handleMouseMove = (e) => {
    if (!isResizing.current) return;

    const newWidth = e.clientX - resizableRef.current.getBoundingClientRect().left;
    
    let _newShow = showTab;
    let _newWidth = newWidth;

    if(!showTab && newWidth > minWidth){
      _newShow = true;
    }
    if(_newWidth < minWidth){
      if(_newWidth < 0){
        _newShow = false;
        _newWidth = 0;
      }else{
        _newWidth = minWidth;
      }
    }else{
      _newWidth = ZMath.Cap(newWidth, minWidth, maxWidth);
    }

    _set(_newShow, _newWidth);
  };

  const handleMouseUp = () => {
    setHover(false);
    isResizing.current = false;
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);
  };

  const onMouseEnter = () => {
    setHover(true);
  };

  const onMouseLeave = () => {
    if(!isResizing.current){
      setHover(false);
    }
  };

  const _tabWidth = showTab ? tabWidth : 0;

  function renderMobile(){
    return (
      <VStack fullHeight fullWidth>
        <HStack fullWidth lockHeight={50} bW={[0, 1, 0, 0]}
          HAlign="["
          flexShrink={0}
          bColor={ColorX.CSS("greyOut", 0.2)}
          colorTransition
          bgColor={ColorX.DayNight({
            day: "cmBgBlue",
            night: "dmBgDark"
          }, 1, darkMode)}>
          {renderChildren(children)}
        </HStack>
        <HStack fullWidth fullHeight bW={0}
          display={showTab ? "flex" : "none"}
          bColor={ColorX.CSS("greyOut", 0.2)} forwardRef={resizableRef}>
          <VStack fullWidth fullHeight>
            {renderMain(children)}
          </VStack>
        </HStack>
      </VStack>
    );
  }

  function renderNormal(){
    return (
      <HStack fullHeight lockWidth={50 + _tabWidth} 
        position="relative" overflow="visible">
        <VStack lockWidth={50} fullHeight bW={[0, 1, 0, 0]} 
          flexShrink={0}
          bColor={ColorX.CSS("greyOut", 0.2)}
          colorTransition
          bgColor={ColorX.DayNight({
            day: "cmBgBlue",
            night: "dmBgDark"
          }, 1, darkMode)}>
          {renderChildren(children)}
        </VStack>
        <HStack lockWidth={_tabWidth} fullHeight bW={showTab ? [0, 1, 0, 0] : 0}
          bColor={ColorX.CSS("greyOut", 0.2)} forwardRef={resizableRef}>
          <VStack width={_tabWidth} fullHeight>
            {renderMain(children)}
          </VStack>
          <XDiv position="absolute" style={{right: 0}} fullHeight width={0} 
            overflow="visible" zIndex={1}>
            <XDiv 
              colorTransition
              onMouseEnter={onMouseEnter} 
              onMouseLeave={onMouseLeave}
              onMouseDown={handleMouseDown}
              cursor="ew-resize"
              width={hover ? 4 : 2}
              mL={hover ? -2 : -1}
              fullHeight
              bgColor={ColorX.CSS(hover ? "cmBlue" : "transparent")}
            />
          </XDiv>
        </HStack>
      </HStack>
    );
  }

  if(responsive){
    return ResponsiveX.Split({
      0: renderMobile,
      [SysConfig.WebUIWidth]: renderNormal
    });
  }else{
    return renderNormal();
  }

}

export default QPMenu;